import { filterFalsy } from '@/utils/arrayUtils';

import type { TChecklistItem } from '@/types/common';
import type { TStationAmenity } from '@/types/station';

export const amenitiesToForm = (value: TStationAmenity[] = []): Record<string, boolean> =>
  value.reduce(
    (memo, { type, items }) => ({
      ...memo,
      ...items?.reduce((acc, item) => ({ ...acc, [item]: true }), {}),
      [type]: true,
    }),
    {},
  );

/**
 * Turns the checked boxes of the Amenities form into the `amenities` list sent to the backend.
 *
 * Why `currentAmenities`: the form only knows type and items. Each saved amenity can also carry a
 * `location` (its zone shape) and other backend fields the portal does not edit. If we sent only
 * type and items, the backend could erase the zones, and the Zones card and the Visits heatmap
 * would lose them. So an amenity that stays checked is spread from its current object, and the
 * form values for `type` and `items` win.
 *
 * Constraints:
 * - Match is by `type` (the option name).
 * - A newly checked amenity has no current object, so it is sent without `location`.
 * - An unchecked amenity is dropped, with its location.
 * - Callers with no saved amenities (Add a Site wizard) omit `currentAmenities`. The output is
 *   then only `{ type, items }`, as before.
 */
export const formToAmenities = (
  value: Record<string, boolean>,
  options: TChecklistItem[],
  currentAmenities: TStationAmenity[] = [],
) =>
  options
    .map<TStationAmenity | false>(({ name, nested }) => {
      if (!value[name]) {
        return false;
      }

      return {
        ...currentAmenities.find(({ type }) => type === name),
        type: name,
        items: nested?.map((item) => item.name).filter((item) => value[item]),
      };
    })
    .filter(filterFalsy);
